import { type ApiClient, createApiClient, createQueryClient } from "@luckymenu/api-client";
import { ApiProvider, createQueryKeys } from "@luckymenu/api-client/react";
import type { RealtimeClient } from "@luckymenu/realtime-client";
import type { QueryClient } from "@tanstack/react-query";
import { createContext, type ReactNode, useCallback, useContext, useState } from "react";
import { useStore } from "zustand";
import type { StaffPlatform } from "./adapters";
import { createMenuDeviceCache, type MenuDeviceCache } from "./menu/device-cache";
import type { Outbox } from "./ordering/outbox";
import { createStaffOutbox } from "./ordering/outbox-client";
import { PrintService } from "./printing/service";
import { deviceIdOf } from "./push/offer";
import { createStaffRealtime } from "./realtime/connection";
import {
	type ActiveRestaurantState,
	type ActiveRestaurantStore,
	createActiveRestaurantStore,
} from "./stores/active-restaurant";
import { type AppGateStore, createAppGateStore, watchAppGate } from "./stores/app-gate";
import { createOrderDraftsStore, type OrderDraftsStore } from "./stores/order-drafts";
import { createPushPromptStore, type PushPromptStore } from "./stores/push-prompt";
import {
	createStaffSwitchStore,
	type StaffSwitchStore,
	switchTokenFor,
} from "./stores/staff-switch";
import {
	createSupportSessionStore,
	type SupportSession,
	type SupportSessionState,
	type SupportSessionStore,
} from "./stores/support-session";

type StaffCore = {
	platform: StaffPlatform;
	client: ApiClient;
	queryClient: QueryClient;
	activeRestaurant: ActiveRestaurantStore;
	support: SupportSessionStore;
	/** The live connection, when the platform provides one. Started by `useLiveConnection`. */
	realtime: RealtimeClient | null;
	/** Order drafts, kept in storage. */
	drafts: OrderDraftsStore;
	/** The menu kept on the device (menu/device-cache.ts). */
	menuCache: MenuDeviceCache;
	/** When to offer push notifications (push/offer.ts). */
	pushPrompt: PushPromptStore;
	/** Sends to the kitchen with one request ID per send (ordering/outbox.ts). */
	outbox: Outbox;
	/** This install's device ID (`luckymenu.deviceId`), made once and kept. */
	deviceId: () => Promise<string>;
	/** Printing on this device and as a print station (printing/service.ts). */
	print: PrintService;
	/** A quick switch to another member with their PIN (P6-T11). */
	staffSwitch: StaffSwitchStore;
	/** App-wide answers: update required (P6-T13). */
	appGate: AppGateStore;
};

/** Drops every cached query of the restaurant (or person) in use, before a switch. */
function dropRestaurantQueries(queryClient: QueryClient): void {
	queryClient.removeQueries({
		predicate: (query) => {
			const path = query.queryKey[0];
			return !(Array.isArray(path) && RESTAURANT_FREE.has(path.join(".")));
		},
	});
}

const StaffCoreContext = createContext<StaffCore | null>(null);

/** Builds the shared services from the platform adapters. */
export function createStaffCore(
	platform: StaffPlatform,
	options: { fetch?: typeof fetch; support?: SupportSession | null } = {},
): StaffCore {
	const activeRestaurant = createActiveRestaurantStore(platform.storage);
	const support = createSupportSessionStore(options.support ?? null);
	const staffSwitch = createStaffSwitchStore(platform.storage);
	const appGate = createAppGateStore();
	let knownDeviceId: Promise<string> | null = null;
	const deviceId = () => {
		knownDeviceId ??= deviceIdOf(platform.storage);
		return knownDeviceId;
	};
	const client = createApiClient({
		getDeviceId: deviceId,
		url: platform.apiUrl,
		getToken: () => platform.auth.getToken(),
		// A support session names its restaurant; otherwise the chosen one.
		getRestaurantId: () =>
			support.getState().session?.restaurantId ?? activeRestaurant.getState().restaurantId,
		getSupportToken: () => support.getState().session?.token ?? null,
		// A quick switch counts only in its own restaurant, and never in a support session.
		getStaffSwitchToken: () =>
			support.getState().session
				? null
				: switchTokenFor(
						staffSwitch.getState().active,
						activeRestaurant.getState().restaurantId,
						Date.now(),
					),
		appVersion: platform.appVersion,
		...(platform.appBuild ? { appBuild: platform.appBuild } : {}),
		...(options.fetch ? { fetch: options.fetch } : {}),
	});
	const queryClient = createQueryClient();
	watchAppGate(queryClient, appGate, () => staffSwitch.getState().set(null));
	// Another person acting means another role: nothing cached for the previous one is kept.
	staffSwitch.subscribe((state, previous) => {
		if (state.active?.token !== previous.active?.token) dropRestaurantQueries(queryClient);
	});
	const realtime = platform.realtime
		? createStaffRealtime(platform.realtime, queryClient, async () => {
				// The same restaurant and support token as the API calls.
				const session = support.getState().session;
				const restaurantId = session?.restaurantId ?? activeRestaurant.getState().restaurantId;
				if (!restaurantId) return null;
				const token = await platform.auth.getToken();
				if (!token) return null;
				return { token, restaurantId, supportToken: session?.token ?? null };
			})
		: null;
	const currentRestaurant = () =>
		support.getState().session?.restaurantId ?? activeRestaurant.getState().restaurantId;
	const keys = createQueryKeys(client, queryClient);
	const outbox = createStaffOutbox({
		platform,
		client,
		queryClient,
		keys,
		realtime,
		activeRestaurant: currentRestaurant,
	});
	const drafts = createOrderDraftsStore(platform.storage);
	const menuCache = createMenuDeviceCache({
		storage: platform.storage,
		queryClient,
		queryKey: keys.staff.catalog.listMenu.queryKey(),
		restaurantId: currentRestaurant,
	});
	return {
		platform,
		client,
		queryClient,
		activeRestaurant,
		support,
		realtime,
		drafts,
		outbox,
		menuCache,
		pushPrompt: createPushPromptStore(platform.storage),
		staffSwitch,
		appGate,
		deviceId,
		print: new PrintService({
			platform,
			client,
			deviceId,
			restaurantId: currentRestaurant,
			realtime,
		}),
	};
}

/**
 * Wires the API client, TanStack Query and shared stores for a staff app.
 * Both apps render this once at the root, inside their Clerk provider.
 */
export function StaffCoreProvider({
	platform,
	core: given,
	children,
}: {
	platform: StaffPlatform;
	/** For tests: a prebuilt core, for example with a fake fetch. */
	core?: StaffCore;
	children: ReactNode;
}) {
	const [core] = useState(() => given ?? createStaffCore(platform));
	return (
		<StaffCoreContext value={core}>
			<ApiProvider client={core.client} queryClient={core.queryClient}>
				{children}
			</ApiProvider>
		</StaffCoreContext>
	);
}

/** For staff-core's own hooks. */
export function useStaffCore(): StaffCore {
	const core = useContext(StaffCoreContext);
	if (!core) throw new Error("Wrap the app in <StaffCoreProvider>");
	return core;
}

export function usePlatform(): StaffPlatform {
	return useStaffCore().platform;
}

export function useActiveRestaurant<T>(select: (state: ActiveRestaurantState) => T): T {
	return useStore(useStaffCore().activeRestaurant, select);
}

/** Query keys that do not depend on the active restaurant: they survive a switch. */
const RESTAURANT_FREE = new Set(["staff.identity.me", "staff.tenancy.listMyRestaurants"]);

/**
 * Makes `restaurantId` the active restaurant (or none), and drops every cached
 * query that belongs to the previous one. tRPC query keys do not contain the
 * `x-restaurant-id` header, so the cache must be cleared on a switch.
 */
export function useSwitchRestaurant(): (restaurantId: string | null) => Promise<void> {
	const core = useStaffCore();
	return useCallback(
		async (restaurantId) => {
			if (core.activeRestaurant.getState().restaurantId === restaurantId) return;
			// Clear first: queries for the new restaurant start as soon as it is chosen.
			dropRestaurantQueries(core.queryClient);
			await core.activeRestaurant.getState().choose(restaurantId);
			// Sends waiting for this restaurant go now.
			void core.outbox.flush();
		},
		[core],
	);
}

export function useSupportSession<T>(select: (state: SupportSessionState) => T): T {
	return useStore(useStaffCore().support, select);
}

/** Starts or ends a support session, dropping every cached query of the previous context. */
export function useSetSupportSession(): (session: SupportSession | null) => void {
	const core = useStaffCore();
	return useCallback(
		(session) => {
			core.queryClient.removeQueries({
				predicate: (query) => {
					const path = query.queryKey[0];
					return !(Array.isArray(path) && path.join(".") === "staff.identity.me");
				},
			});
			if (session) core.support.getState().start(session);
			else core.support.getState().end();
		},
		[core],
	);
}
